import { useState } from 'react';
import { trackAffiliateClick } from '../../../utils/analytics';

interface FAQItem {
  question: string;
  answer: string;
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: "What exactly do I get for $30/month?",
      answer: "You get unlimited phone consultations with attorneys, contract and document review (up to 15 pages), demand letters sent on law firm letterhead, traffic ticket defense, trial defense (civil and criminal), IRS audit assistance, and 25% off additional legal services. It's like having a law firm on retainer for less than a gym membership."
    },
    {
      question: "Is this real lawyers or just legal advice?",
      answer: "Real, licensed attorneys in your state. Not paralegals, not AI, not a call center reading scripts. When you call, you're talking to an actual attorney who can represent you in court if needed."
    },
    {
      question: "What if I need a lawyer for something not covered?",
      answer: "You get 25% off their normal hourly rate. But honestly, most everyday legal problems (contractor disputes, landlord issues, traffic tickets, demand letters) are fully covered. The discount is there for the big stuff like divorce or major lawsuits."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes. Month-to-month. No contracts, no cancellation fees. If you don't need it anymore, cancel. But most people keep it because $30/month is cheaper than one hour with a lawyer."
    },
    {
      question: "Does this work in my state?",
      answer: "LegalShield operates in all 50 states and Canada. When you sign up, you're matched with attorneys licensed in your state who know your local laws."
    },
    {
      question: "What's the difference between Personal, Landlord/Business, and ID Shield?",
      answer: "Personal covers you and your family for everyday legal issues. Landlord/Business adds coverage for rental properties and small business matters. ID Shield is identity theft protection with restoration services. Most people start with Personal, then add the others as needed."
    },
    {
      question: "How fast can I use it after signing up?",
      answer: "Immediately for consultations and document review. Demand letters and representation services are available after a short waiting period (usually 30 days for pre-existing matters). But if something new happens after you join, you're covered right away."
    },
    {
      question: "Is this insurance or a membership?",
      answer: "It's a membership, not insurance. Insurance pays claims after something bad happens. This gives you access to attorneys before, during, and after problems arise. It's proactive legal protection, not reactive coverage."
    }
  ];

  return (
    <section id="faq" className="relative py-20 bg-[#0f1d2e]">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="bg-[#DC2626]/10 border border-[#DC2626] rounded-full px-6 py-2">
              <p className="text-[#DC2626] font-black text-sm uppercase tracking-wider">
                Questions Answered
              </p>
            </div>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white mb-4" style={{ fontFamily: "'Roboto', sans-serif" }}>
            FREQUENTLY ASKED <span className="text-[#DC2626]">QUESTIONS</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Everything you need to know before activating your shield.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-[#0a1628] border-2 border-[#DC2626]/20 rounded-xl overflow-hidden transition-all duration-300 hover:border-[#DC2626]/40"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left cursor-pointer"
              >
                <h3 className="text-lg md:text-xl font-bold text-white pr-4">
                  {faq.question}
                </h3>
                <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-[#DC2626]/20 flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-[#DC2626] text-2xl"></i>
                </div>
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-6">
            Still have questions? Ready to get protected?
          </p>
          <button
            onClick={() => {
              trackAffiliateClick('FAQ CTA', '/#chooser', 'Home');
              const element = document.getElementById('chooser');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="inline-flex items-center gap-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-black text-lg px-8 py-4 rounded-lg transition-all duration-300 shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            ACTIVATE MY SHIELD
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
